// Region-specific ad budget answers (global keeps INR figures; usa uses USD).
const AD_BUDGET_ANSWERS = {
  global:
    "We recommend a minimum of ₹15,000-20,000 per month for effective campaigns. This covers ad spend and management. We can scale based on your goals and results.",
  usa:
    "We recommend a minimum of $800–1,200 per month for effective campaigns. This covers ad spend and management. We can scale based on your goals and results.",
};

const baseServiceFAQs = {
  "Menu Management": [
    {
      question: "How often can I update my menu?",
      answer: "You can update your menu as frequently as needed. We recommend weekly updates for specials and seasonal changes, with major revisions quarterly. Our system makes updates quick and easy.",
    },
    {
      question: "Will you help with menu design and pricing?",
      answer: "Yes! We provide comprehensive menu consulting including item positioning, pricing strategy based on cost analysis, and visual design to maximize appeal and profitability.",
    },
    {
      question: "Can you integrate with my existing POS system?",
      answer: "Absolutely. We work with all major POS systems and ensure seamless integration so your menu updates automatically sync across all platforms.",
    },
    {
      question: "What's the typical timeframe for menu optimization?",
      answer: "Initial menu analysis takes 1-2 weeks. Implementation of changes happens in phases over 3-4 weeks to monitor customer response and make adjustments.",
    },
    {
      question: "Do you provide menu photography services?",
      answer: "Yes, professional food photography is available as an add-on service. High-quality images significantly boost online orders and customer engagement.",
    },
  ],
  "Website Design": [
    {
      question: "How long does it take to build a website?",
      answer: "A standard restaurant website takes 4-6 weeks from design approval to launch. This includes design, development, content creation, and testing.",
    },
    {
      question: "Will my website be mobile-friendly?",
      answer: "Yes, all our websites are fully responsive and optimized for mobile devices. Over 70% of food-related searches happen on mobile, so this is essential.",
    },
    {
      question: "Can customers order online through the website?",
      answer: "Yes! We integrate online ordering systems with payment gateways, allowing customers to place orders directly. We can also integrate with delivery platforms.",
    },
    {
      question: "Do you provide website maintenance?",
      answer: "Yes, we offer ongoing maintenance packages that include updates, security patches, content changes, and technical support to keep your site running smoothly.",
    },
    {
      question: "Will I be able to update content myself?",
      answer: "Absolutely. We build websites with user-friendly content management systems (CMS) and provide training so you can easily update menus, images, and text.",
    },
  ],
  "Digital Marketing": [
    {
      question: "What platforms do you advertise on?",
      answer: "We run campaigns on Google Ads, Facebook, Instagram, and local listing platforms. We choose platforms based on where your target customers are most active.",
    },
    {
      question: "How do you measure marketing success?",
      answer: "We track key metrics including reach, engagement, website traffic, conversion rates, and ROI. You'll receive detailed monthly reports showing exactly how campaigns perform.",
    },
    {
      question: "What's the minimum budget for digital marketing?",
      answer: AD_BUDGET_ANSWERS.global,
    },
    {
      question: "How quickly will I see results?",
      answer: "Initial traction typically appears within 2-3 weeks. Significant results like increased footfall and orders usually materialize within 2-3 months as we optimize campaigns.",
    },
    {
      question: "Do you handle social media management too?",
      answer: "Yes! Our digital marketing includes social media strategy, content creation, posting schedules, community engagement, and influencer collaborations.",
    },
  ],
  "Staff Training": [
    {
      question: "How long are the training programs?",
      answer: "Training duration varies by role and skill level. Typical programs run 2-4 weeks with mix of classroom and on-job training. We customize based on your needs.",
    },
    {
      question: "Do you provide certificates?",
      answer: "Yes, all participants receive certificates upon successful completion. We also provide skill assessments before and after training to measure improvement.",
    },
    {
      question: "Can training be done during business hours?",
      answer: "We offer flexible scheduling including after-hours, weekend, or split-shift training to minimize disruption to your operations.",
    },
    {
      question: "What topics do you cover?",
      answer: "Training covers customer service, product knowledge, POS systems, hygiene standards, sales techniques, conflict resolution, and industry best practices.",
    },
    {
      question: "Do you provide refresher training?",
      answer: "Yes! We offer quarterly refresher sessions and ongoing support to reinforce learning and introduce new techniques as your business evolves.",
    },
  ],
  "Infrastructure": [
    {
      question: "What does infrastructure maintenance include?",
      answer: "We cover equipment maintenance, facility management, safety compliance, vendor coordination, preventive maintenance schedules, and emergency repair support.",
    },
    {
      question: "How do you minimize downtime?",
      answer: "We implement preventive maintenance schedules, keep backup equipment recommendations, maintain vendor relationships for quick repairs, and provide 24/7 emergency support.",
    },
    {
      question: "Do you handle equipment procurement?",
      answer: "Yes, we assist with sourcing, vendor negotiations, quality checks, and installation of kitchen equipment, refrigeration, POS systems, and other infrastructure needs.",
    },
    {
      question: "What's your response time for emergencies?",
      answer: "For critical equipment failures, we aim for 2-4 hour response time. We maintain a network of trusted vendors for rapid deployment and repairs.",
    },
    {
      question: "Can you help with store layout optimization?",
      answer: "Absolutely. We analyze customer flow, optimize space utilization, suggest equipment placement, and recommend improvements to enhance efficiency and customer experience.",
    },
  ],
  "POS Integration": [
    {
      question: "Which POS systems do you work with?",
      answer: "We integrate with major systems like Petpooja, Posist, Toast, Square, and custom solutions. If you have a specific POS, we can likely work with it.",
    },
    {
      question: "How long does integration take?",
      answer: "Standard POS integration takes 1-2 weeks including setup, testing, staff training, and data migration from your old system if needed.",
    },
    {
      question: "Will my old data be migrated?",
      answer: "Yes, we handle complete data migration including menu items, pricing, customer data, and historical sales data to ensure business continuity.",
    },
    {
      question: "Do you provide training for staff?",
      answer: "Yes! Comprehensive training is included for all staff levels - from servers to managers. We ensure everyone is comfortable with the new system before going live.",
    },
    {
      question: "What happens if the system goes down?",
      answer: "We provide 24/7 technical support and maintain backup protocols. Most POS systems have offline modes to continue operations during connectivity issues.",
    },
  ],
  "Purchase Management": [
    {
      question: "How do you reduce procurement costs?",
      answer: "We negotiate better rates with vendors, identify bulk purchase opportunities, reduce wastage through demand forecasting, and find alternative suppliers for cost savings.",
    },
    {
      question: "Do you handle vendor relationships?",
      answer: "Yes, we manage vendor communications, negotiate contracts, monitor performance, resolve disputes, and continuously seek better alternatives to optimize your supply chain.",
    },
    {
      question: "Can you integrate with inventory systems?",
      answer: "Absolutely. We connect procurement with your inventory management for automatic reordering, stock level alerts, and wastage tracking.",
    },
    {
      question: "How do you ensure quality control?",
      answer: "We establish quality standards, conduct vendor audits, implement receiving protocols, and track quality metrics to ensure you receive consistent, high-quality supplies.",
    },
    {
      question: "What reporting do you provide?",
      answer: "Monthly reports include cost analysis, vendor performance, savings achieved, inventory turnover, and recommendations for further optimization.",
    },
  ],
  "CRM": [
    {
      question: "How does CRM increase customer loyalty?",
      answer: "CRM helps personalize communication, track preferences, send targeted offers, manage loyalty programs, and gather feedback - all building stronger customer relationships.",
    },
    {
      question: "What customer data do you collect?",
      answer: "We track visit frequency, order history, preferences, birthdays, feedback, and communication preferences - all while maintaining privacy compliance and data security.",
    },
    {
      question: "Can I send automated marketing messages?",
      answer: "Yes! Set up automated birthday wishes, anniversary offers, re-engagement campaigns, and personalized promotions based on customer behavior and preferences.",
    },
    {
      question: "How do you measure CRM success?",
      answer: "We track metrics like repeat visit rate, customer lifetime value, redemption rates, feedback scores, and referral rates to measure relationship strength.",
    },
    {
      question: "Is it integrated with our POS?",
      answer: "Yes, CRM integrates seamlessly with your POS to automatically capture transaction data, update customer profiles, and trigger personalized communications.",
    },
  ],
  "Offers & Promotions": [
    {
      question: "How do you decide which offers to run?",
      answer: "We analyze sales data, customer behavior, competitor activities, and seasonal trends to design promotions that drive traffic during slow periods and maximize revenue.",
    },
    {
      question: "Can you handle the entire promotion lifecycle?",
      answer: "Yes! From strategy and design to execution, marketing, tracking, and post-campaign analysis - we manage everything to ensure successful promotions.",
    },
    {
      question: "How do you measure promotion effectiveness?",
      answer: "We track redemption rates, incremental revenue, new customer acquisition, repeat visits, and overall ROI to assess each promotion's success.",
    },
    {
      question: "What types of promotions work best?",
      answer: "This varies by business, but typically combo offers, time-limited deals, loyalty rewards, and first-time customer discounts perform well. We test and optimize based on your data.",
    },
    {
      question: "How often should we run promotions?",
      answer: "We recommend strategic promotions 2-4 times per month, balanced with regular pricing to maintain margins while driving consistent customer engagement.",
    },
  ],
};

// Returns the FAQ data for a region ("usa" | "global"). Only the digital
// marketing ad-budget answer differs between regions today.
export const getServiceFAQs = (region) => ({
  ...baseServiceFAQs,
  "Digital Marketing": baseServiceFAQs["Digital Marketing"].map((faq) =>
    faq.question === "What's the minimum budget for digital marketing?"
      ? { ...faq, answer: AD_BUDGET_ANSWERS[region] || AD_BUDGET_ANSWERS.global }
      : faq
  ),
});
